import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { analyticsAPI, patientAPI } from '../services/api';
import StatCard from '../components/StatCard';

const Analytics = () => {
  const [timeTracking, setTimeTracking] = useState(null);
  const [patientTimeTracking, setPatientTimeTracking] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchAnalytics();
    fetchPatients();
  }, [dateRange]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchPatientTimeTracking();
    }
  }, [tabValue, dateRange, selectedPatient]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getTimeTracking(dateRange);
      setTimeTracking(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await patientAPI.getPatients({ limit: 100 });
      setPatients(response.data.data);
    } catch (err) {
      console.error('Failed to fetch patients');
    }
  };

  const fetchPatientTimeTracking = async () => {
    try {
      setLoading(true);
      const params = { ...dateRange };
      if (selectedPatient) {
        params.patientId = selectedPatient._id;
      }
      const response = await analyticsAPI.getPatientTimeTracking(params);
      setPatientTimeTracking(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch patient time tracking');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="700">
          Analytics
        </Typography>
      </Box>

      {/* Date Range Filter */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2, boxShadow: 'none' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          Date Range Filter
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for different views */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, boxShadow: 'none' }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 'bold',
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Overall Analytics" />
          <Tab label="Per-Patient Time Tracking" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="700">
                  Average Time by Stage
                </Typography>
              </Box>
              {timeTracking?.avgTimeByStage?.length > 0 ? (
                <Box>
                  {timeTracking.avgTimeByStage.map((stage) => {
                    const maxTime = Math.max(...timeTracking.avgTimeByStage.map(s => s.avgDuration));
                    const percentage = (stage.avgDuration / maxTime) * 100;

                    return (
                      <Box key={stage._id} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize' }}>
                            {stage._id}
                          </Typography>
                          <Typography variant="body2" fontWeight="700" color="primary.main">
                            {formatTime(Math.round(stage.avgDuration))}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          color="primary"
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#E9E9F2',
                            '& .MuiLinearProgress-bar': { borderRadius: 3 }
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No data available
                </Typography>
              )}
          </Paper>
        </Grid>

          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <SpeedIcon sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Bottlenecks (Longest Stages)
                </Typography>
              </Box>
            {timeTracking?.bottlenecks?.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                {timeTracking.bottlenecks.map((stage, index) => (
                  <Box
                    key={stage._id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                        #{index + 1} {stage._id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stage.count} visits
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold" color="error">
                      {formatTime(Math.round(stage.avgDuration))}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No bottlenecks identified
              </Typography>
            )}
          </Paper>
        </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Daily Visit Volume
                </Typography>
              </Box>
            {timeTracking?.dailyVisits?.length > 0 ? (
              <Box sx={{ mt: 2, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ textAlign: 'left', padding: '12px' }}>Date</th>
                      <th style={{ textAlign: 'right', padding: '12px' }}>Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeTracking.dailyVisits.map((day) => (
                      <tr key={day._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px' }}>{day._id}</td>
                        <td style={{ textAlign: 'right', padding: '12px', fontWeight: 'bold' }}>
                          {day.count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No data available
              </Typography>
            )}
          </Paper>
        </Grid>
        </Grid>
      )}

      {/* Per-Patient Time Tracking Tab */}
      {tabValue === 1 && (
        <Box>
          {/* Patient Filter */}
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2, boxShadow: 'none' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              Patient Filter
            </Typography>
            <Autocomplete
              options={patients}
              getOptionLabel={(option) => `${option.name} (${option.patientId})`}
              value={selectedPatient}
              onChange={(e, newValue) => setSelectedPatient(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Select Patient (Optional)" 
                  placeholder="Search by name or ID..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            />
          </Paper>

          {/* Stats Cards */}
          {patientTimeTracking && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <StatCard
                label="Total Patients"
                value={patientTimeTracking.avgTimePerPatient?.length || 0}
                icon={<PersonIcon fontSize="small" />}
                color="primary"
                subtitle="In selected period"
              />
              <StatCard
                label="Total Visits"
                value={patientTimeTracking.patientTimeTracking?.length || 0}
                icon={<TrendingUpIcon fontSize="small" />}
                color="info"
                subtitle="All patient visits"
              />
              <StatCard
                label="Avg Visit Time"
                value={formatTime(Math.round(
                  patientTimeTracking.avgTimePerPatient?.reduce((sum, p) => sum + (p.avgTotalTime || 0), 0) /
                  (patientTimeTracking.avgTimePerPatient?.length || 1)
                ))}
                icon={<TimeIcon fontSize="small" />}
                color="success"
                subtitle="Per patient"
              />
              <StatCard
                label="Longest Visit"
                value={formatTime(Math.round(
                  Math.max(...(patientTimeTracking.avgTimePerPatient?.map(p => p.avgTotalTime || 0) || [0]))
                ))}
                icon={<SpeedIcon fontSize="small" />}
                color="secondary"
                subtitle="Maximum time"
              />
            </Box>
          )}

          {/* Patient Time Tracking Table */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Patient Visit Details
                </Typography>
                {patientTimeTracking?.patientTimeTracking?.length > 0 ? (
                  <TableContainer sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell><strong>Visit ID</strong></TableCell>
                          <TableCell><strong>Patient</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell align="right"><strong>Total Time</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {patientTimeTracking.patientTimeTracking.slice(0, 20).map((visit) => (
                          <TableRow key={visit._id} hover>
                            <TableCell>{visit.visitId}</TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {visit.patientName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {visit.patientId} • {visit.patientAge}y • {visit.patientGender}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {new Date(visit.visitDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={visit.status.replace(/-/g, ' ')} 
                                size="small"
                                color={visit.status === 'completed' ? 'success' : 'primary'}
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                {formatTime(visit.totalVisitTime || 0)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    No patient visit data available
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Top Longest and Shortest Visits */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" color="error">
                  Top 5 Longest Average Visits
                </Typography>
                {patientTimeTracking?.topLongestVisits?.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {patientTimeTracking.topLongestVisits.slice(0, 5).map((patient, index) => (
                      <Box
                        key={patient._id}
                        sx={{
                          p: 2,
                          mb: 2,
                          borderRadius: 2,
                          bgcolor: 'grey.50',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            #{index + 1} {patient.patientName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {patient.patientId} • {patient.totalVisits} visits
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight="bold" color="error">
                          {formatTime(Math.round(patient.avgTotalTime || 0))}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    No data available
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, boxShadow: 'none' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" color="success.main">
                  Top 5 Shortest Average Visits
                </Typography>
                {patientTimeTracking?.topShortestVisits?.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {patientTimeTracking.topShortestVisits.slice(0, 5).map((patient, index) => (
                      <Box
                        key={patient._id}
                        sx={{
                          p: 2,
                          mb: 2,
                          borderRadius: 2,
                          bgcolor: 'grey.50',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            #{index + 1} {patient.patientName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {patient.patientId} • {patient.totalVisits} visits
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          {formatTime(Math.round(patient.avgTotalTime || 0))}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    No data available
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default Analytics;
